import { geoMercator, geoAlbersUsa } from 'd3-geo';

/**
 * Southern California bounds
 */
export const SOCAL_BOUNDS = {
  minLat: 32.5,
  maxLat: 36.2,
  minLon: -120.6,
  maxLon: -114.5,
  centerLat: 34.0,
  centerLon: -118.0
};

/**
 * Regional bounds for major areas
 */
export const REGIONAL_BOUNDS = {
  socal: SOCAL_BOUNDS,
  southwest: {
    minLat: 31.0,
    maxLat: 37.0,
    minLon: -125.0,
    maxLon: -109.0,
    centerLat: 34.0,
    centerLon: -112.0
  },
  west: {
    minLat: 32.0,
    maxLat: 49.0,
    minLon: -125.0,
    maxLon: -104.0,
    centerLat: 40.0,
    centerLon: -114.0
  }
};

/**
 * Create Albers USA projection (STANDARD FOR US MAPS)
 */
export function createUSProjection() {
  return geoAlbersUsa()
    .scale(1300)
    .translate([0, 0]);
}

/**
 * Create Mercator projection for SoCal (legacy support)
 */
export function createProjection() {
  return geoMercator()
    .center([SOCAL_BOUNDS.centerLon, SOCAL_BOUNDS.centerLat])
    .scale(22000)
    .translate([0, 0]);
}

/**
 * Project lat/lon coordinates to 3D space using Albers USA
 * Returns [x, y, z] suitable for Three.js
 */
export function projectTo3D(lat, lon, elevation = 0) {
  const projection = createUSProjection();
  const projected = projection([lon, lat]);
  
  if (!projected) {
    console.warn(`Coordinates outside projection: lat=${lat}, lon=${lon}`);
    return [0, elevation, 0];
  }
  
  return [
    projected[0] / 100,
    elevation,
    projected[1] / 100
  ];
}

/**
 * Project multiple coordinates
 */
export function projectCoordinates(coordinates) {
  return coordinates.map(([lon, lat]) => {
    const [x, _, z] = projectTo3D(lat, lon);
    return [x, z];
  });
}

/**
 * Calculate distance between two lat/lon points (in km)
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth radius in km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Get camera position for optimal view
 */
export function getOptimalCameraPosition(region = 'socal') {
  const positions = {
    socal: {
      position: [0, 22, 28],   // CENTERED, HIGHER UP
      target: [0, 0, 0],       // CENTERED TARGET
      fov: 50
    },
    us: {
      position: [0, 30, 35],
      target: [0, 0, 0],
      fov: 52
    }
  };
  
  return positions[region] || positions.us;
}

/**
 * Check if coordinates are within a region
 */
export function isWithinRegion(lat, lon, region = 'socal') {
  const bounds = REGIONAL_BOUNDS[region] || SOCAL_BOUNDS;
  
  return (
    lat >= bounds.minLat &&
    lat <= bounds.maxLat &&
    lon >= bounds.minLon &&
    lon <= bounds.maxLon
  );
}

/**
 * Check if coordinates are within SoCal bounds
 */
export function isWithinSoCal(lat, lon) {
  return isWithinRegion(lat, lon, 'socal');
}

/**
 * Get random point within a region (for testing)
 */
export function getRandomPointInRegion(region = 'socal') {
  const bounds = REGIONAL_BOUNDS[region] || SOCAL_BOUNDS;
  
  const lat = bounds.minLat + Math.random() * (bounds.maxLat - bounds.minLat);
  const lon = bounds.minLon + Math.random() * (bounds.maxLon - bounds.minLon);
  
  return { lat, lon };
}

/**
 * Get random point within SoCal bounds (legacy)
 */
export function getRandomSoCalPoint() {
  return getRandomPointInRegion('socal');
}

/**
 * Convert 3D position back to lat/lon (inverse projection)
 */
export function inverseProjection(x, z) {
  const projection = createUSProjection();
  const inverted = projection.invert([x * 100, z * 100]);
  
  if (!inverted) {
    return null;
  }
  
  return {
    lat: inverted[1],
    lon: inverted[0]
  };
}

/**
 * Get bounding box for a set of coordinates
 */
export function getBoundingBox(coordinates) {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }
  
  const lats = coordinates.map(c => c.lat || c[1]);
  const lons = coordinates.map(c => c.lon || c[0]);
  
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
    centerLat: (Math.min(...lats) + Math.max(...lats)) / 2,
    centerLon: (Math.min(...lons) + Math.max(...lons)) / 2
  };
}